import {
    DiscardPendingPeLicenseRequest,
    GetPendingPeLicenseRequest,
    GetPrivateEditionLocalStatus,
    GetPrivateEditionApplianceCurrentResourceUsage,
    GetPrivateEditionApplianceHistoricalResourceUsage,
    InitiatePeLicenseRequest,
    SubmitPeLicenseReceipt,
} from "gc-web-proto/galaxycompletepb/apipb/pelocal_api_pb";
import { useGrpcApiStore } from "../grpc/grpcApiStore";
import { useMutationTrackAndAlertError } from "../core/data/useHooksWithErrorHandling";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ListPrivateEditionVirtualAppliances, SubmitPrivateEditionLicenseRequest } from "gc-web-proto/galaxycompletepb/apipb/pemanage_api_pb";
import { PagerParams } from "gc-web-proto/galaxycompletepb/commonpb/datafilter_pb";

import { getIsPrivateEdition } from "../auth/PrivateEditionView";
import { useCurrentUser, useIsAuthenticated } from "../auth/auth_hooks";
import { useTranslation } from "react-i18next";
import { GetMyProjectRole } from "gc-web-proto/galaxycompletepb/apipb/project_api_pb";
import { ProjectMemberInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/project_pb";
import { RpcError, StatusCode } from "grpc-web";

export const PrivateEditionQueryKeys = {
    InitiatePeLicenseRequest: "initiatePeLicenseRequest",
    GetPrivateEditionLocalStatus: "getPrivateEditionLocalStatus",
    SubmitPeLicenseReceipt: "submitPeLicenseReceipt",
    DiscardPendingPeLicenseRequest: "discardPendingPeLicenseRequest",
    GetPendingPeLicenseRequest: "getPendingPeLicenseRequest",
    ListPeVirtualAppliances: "listPeVirtualAppliances",
    SubmitPeLicenseRequest: "submitPeLicenseRequest",
    GetPrivateEditionLocalCurrentResourceUsage: "getPrivateEditionLocalCurrentResourceUsage",
    GetPrivateEditionLocalHistoricalResourceUsage: "getPrivateEditionLocalHistoricalResourceUsage",
};

export const useGetPrivateEditionLocalStatus = () => {
    const apis = useGrpcApiStore();

    const authenticated = useIsAuthenticated();

    const req = new GetPrivateEditionLocalStatus.Request();
    const queryFn = async () => {
        const res = await apis.privateEditionLocalService.getPrivateEditionLocalStatus(req, null);
        return res?.toObject();
    };
    return useQuery({
        enabled: getIsPrivateEdition() && authenticated, // must be authenticated because this is also used in initialization

        queryKey: [PrivateEditionQueryKeys.GetPrivateEditionLocalStatus],
        queryFn: queryFn,
        refetchInterval: 60 * 1000,
    });
};

export const useInitiatePeLicenseRequest = () => {
    const { t } = useTranslation("privateEdition");
    const apis = useGrpcApiStore();
    const mutationFn = async (args: InitiatePeLicenseRequest.Request) => {
        return await apis.privateEditionLocalService.initiatePeLicenseRequest(args, null);
    };

    const queryClient = useQueryClient();

    return useMutationTrackAndAlertError(
        {
            mutationKey: [PrivateEditionQueryKeys.InitiatePeLicenseRequest],
            mutationFn: mutationFn,
            onSuccess: async () => {
                await queryClient.refetchQueries({
                    queryKey: [PrivateEditionQueryKeys.GetPendingPeLicenseRequest],
                });
            },
        },
        t("progress.generatingRequest")
    );
};

export const usePEApplianceManagement = () => {
    const apis = useGrpcApiStore();
    const user = useCurrentUser();
    const status = useGetPrivateEditionLocalStatus();
    const projectId = status.data?.status?.systemId;
    const role = useQuery({
        queryKey: ["peMainProjectRole", projectId, user?.userId],
        enabled: getIsPrivateEdition() && !!projectId && !!user,
        queryFn: async () => {
            try {
                const res = await apis.projectService.getMyProjectRole(new GetMyProjectRole.Request().setProjectId(projectId), null);
                return res.getMyRole();
            } catch (error) {
                if ((error as RpcError).code === StatusCode.PERMISSION_DENIED) {
                    return -1 as ProjectMemberInfo.Role;
                }
                throw error;
            }
        },
        retry: false,
        refetchInterval: 30 * 1000,
    });
    const hasStatus = getIsPrivateEdition() && !status.isError && !!status.data?.status;
    const isMainAdmin = !role.isError && role.data === ProjectMemberInfo.Role.ADMIN;
    const isMainMember =
        !role.isError &&
        (role.data === ProjectMemberInfo.Role.ADMIN || role.data === ProjectMemberInfo.Role.OPERATOR || role.data === ProjectMemberInfo.Role.MONITOR);
    const canManage = hasStatus && isMainMember;
    return { canManage, isMainAdmin, role, status };
};

export const useSubmitPeLicenseReceipt = () => {
    const { t } = useTranslation("privateEdition");
    const apis = useGrpcApiStore();
    const mutationFn = async (args: SubmitPeLicenseReceipt.Request) => {
        return await apis.privateEditionLocalService.submitPeLicenseReceipt(args, null);
    };
    return useMutationTrackAndAlertError(
        {
            mutationKey: [PrivateEditionQueryKeys.SubmitPeLicenseReceipt],
            mutationFn: mutationFn,
        },
        t("progress.submittingReceipt")
    );
};

export const useDiscardPendingPeLicenseRequest = () => {
    const { t } = useTranslation("privateEdition");
    const apis = useGrpcApiStore();
    const mutationFn = async () => {
        return await apis.privateEditionLocalService.discardPendingPeLicenseRequest(new DiscardPendingPeLicenseRequest.Request(), null);
    };
    return useMutationTrackAndAlertError(
        {
            mutationKey: [PrivateEditionQueryKeys.DiscardPendingPeLicenseRequest],
            mutationFn: mutationFn,
        },
        t("progress.discardingRequest")
    );
};

export const useGetPendingPeLicenseRequest = () => {
    const api = useGrpcApiStore();
    const { canManage } = usePEApplianceManagement();
    const queryFn = async () => {
        const res = await api.privateEditionLocalService.getPendingPeLicenseRequest(new GetPendingPeLicenseRequest.Request(), null);
        return res?.toObject();
    };

    return useQuery({
        queryKey: [PrivateEditionQueryKeys.GetPendingPeLicenseRequest],
        enabled: canManage,
        queryFn: queryFn,
        refetchInterval: 60 * 1000,
    });
};

export const useListPeVirtualAppliances = (projectId: string, page: number, perPage: number) => {
    const api = useGrpcApiStore();
    const queryFn = async () => {
        const req = new ListPrivateEditionVirtualAppliances.Request()
            .setPageParams(new PagerParams().setPage(page).setPerPage(perPage))
            .setProjectId(projectId);
        const res = await api.privateEditionManagementService.listPrivateEditionVirtualAppliances(req, null);
        return res?.toObject();
    };
    return useQuery({
        queryKey: [PrivateEditionQueryKeys.ListPeVirtualAppliances, projectId, page, perPage],
        queryFn: queryFn,
        refetchInterval: 60 * 1000,
    });
};

export const useSubmitPeLicenseRequest = () => {
    const { t } = useTranslation("privateEdition");
    const apis = useGrpcApiStore();
    const queryClient = useQueryClient();
    const mutationFn = async (args: SubmitPrivateEditionLicenseRequest.Request) => {
        const res = await apis.privateEditionManagementService.submitPrivateEditionLicenseRequest(args, null);
        return res?.toObject();
    };

    return useMutationTrackAndAlertError(
        {
            mutationKey: [PrivateEditionQueryKeys.SubmitPeLicenseRequest],
            mutationFn: mutationFn,
            onSuccess: async (data, variables) => {
                if (!variables.getPreview()) {
                    await queryClient.refetchQueries({
                        queryKey: [PrivateEditionQueryKeys.ListPeVirtualAppliances],
                    });
                }
            },
        },
        t("progress.submittingRequest")
    );
};

export const useGetPrivateEditionLocalCurrentResourceUsage = () => {
    const apis = useGrpcApiStore();
    const authenticated = useIsAuthenticated();

    const queryFn = async () => {
        const req = new GetPrivateEditionApplianceCurrentResourceUsage.Request();
        const res = await apis.privateEditionLocalService.getPrivateEditionApplianceCurrentResourceUsage(req, null);
        return res?.toObject();
    };

    return useQuery({
        enabled: getIsPrivateEdition() && authenticated,
        queryKey: [PrivateEditionQueryKeys.GetPrivateEditionLocalCurrentResourceUsage],
        queryFn: queryFn,
        refetchInterval: 5000, // 5 seconds for real-time feel
    });
};

export const useGetPrivateEditionLocalHistoricalResourceUsage = (hours: number) => {
    const apis = useGrpcApiStore();
    const authenticated = useIsAuthenticated();

    const queryFn = async () => {
        const req = new GetPrivateEditionApplianceHistoricalResourceUsage.Request();
        req.setHours(hours);
        const res = await apis.privateEditionLocalService.getPrivateEditionApplianceHistoricalResourceUsage(req, null);
        return res?.toObject();
    };

    return useQuery({
        enabled: getIsPrivateEdition() && authenticated && hours > 0,
        queryKey: [PrivateEditionQueryKeys.GetPrivateEditionLocalHistoricalResourceUsage, hours],
        queryFn: queryFn,
        refetchInterval: 60000, // 1 minute for historical data
    });
};
